import React from "react";
import {
    TextField,
    Button,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Paper,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import ImageUpload from "../ImageUpload";
import DownloadIcon from "@material-ui/icons/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const nriIsHoldingOptions = [
    { value: "indian", label: "Indian Passport" },
    { value: "foreign", label: "Foreign Passport" },
];

const proofOfNriDocumentTypeOptions = [
    {
        value: "valid_visa",
        label:
            "Valid VISA (Employment / Residence / Student / Dependent etc.)",
    },
    {
        value: "work_residence_permit",
        label: "Work / Residence Permit",
    },
    {
        value: "oci_card",
        label: "OCI (Overseas Citizen of India) Card",
    },
    {
        value: "pio_card",
        label: "PIO (Person of Indian Origin) Card",
    },
    {
        value: "pio_declaration",
        label: "PIO Declaration",
    },
];

const ProofOfNRIInformationGrid = (props) => {
    const initialState = {
        nri_is_holding: props?.aadharCardDetails?.nri_is_holding || "",
        proof_of_nri_document_type:
            props?.aadharCardDetails?.proof_of_nri_document_type || "",
        proof_of_nri_document_password:
            props?.aadharCardDetails?.proof_of_nri_document_password || "",
    };

    const [currentState, setCurrentState] = React.useState(initialState);
    const [proofOfNriFile, setProofOfNriFile] = React.useState(null);

    const handleChange = (event) => {
        event.persist();
        setCurrentState((previousValues) => ({
            ...previousValues,
            [event.target.name]: event.target.value,
        }));
    };

    const buildNriPayload = () => {
        const payload = {
            nri_is_holding: currentState.nri_is_holding,
            proof_of_nri_document_type:
                currentState.proof_of_nri_document_type,
            proof_of_nri_document_password:
                currentState.proof_of_nri_document_password || "",
        };

        if (proofOfNriFile instanceof File) {
            payload.proof_of_nri_document = proofOfNriFile;
        }

        return payload;
    };

    const handleNriFileChange = (event) => {
        event.preventDefault();
        setProofOfNriFile(event.target.files[0]);
    };

    function getBase64MimeType(base64) {
        if (!base64) return "";

        if (base64.startsWith("JVBERi0")) return "application/pdf";
        if (base64.startsWith("iVBOR")) return "image/png";
        if (base64.startsWith("/9j/")) return "image/jpeg";

        return "image/jpeg";
    }

    function downloadBase64File(base64, mimeType, fileName = "document") {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        const extension =
            mimeType === "application/pdf"
                ? "pdf"
                : mimeType === "image/png"
                    ? "png"
                    : "jpg";

        link.href = url;
        link.download = `${fileName}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }

    function openBase64InNewTab(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);

        window.open(url, "_blank");
    }

    function downloadNriDocument() {
        const base64 = props?.aadharCardDetails?.proof_of_nri_document;
        if (!base64) return;

        const mimeType = getBase64MimeType(base64);
        downloadBase64File(base64, mimeType, "proof_of_nri_document");
    }

    function openNriDocument() {
        const base64 = props?.aadharCardDetails?.proof_of_nri_document;
        if (!base64) return;

        const mimeType = getBase64MimeType(base64);
        openBase64InNewTab(base64, mimeType);
    }

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="flex-start"
            style={{ padding: "20px" }}
        >
            <Grid item>
                <Paper
                    style={{
                        width: 650,
                        minWidth: 650,
                        maxWidth: 650,
                        padding: "24px",
                        borderRadius: "12px",
                    }}
                >
                    <Typography
                        variant="h5"
                        gutterBottom
                        style={{ fontWeight: 600 }}
                    >
                        Proof of NRI
                    </Typography>

                    <Box>
                        <Box mb={2}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Passport Type (NRI is holding)</InputLabel>
                                <Select
                                    name="nri_is_holding"
                                    value={currentState.nri_is_holding}
                                    onChange={handleChange}
                                    label="Passport Type (NRI is holding)"
                                >
                                    {nriIsHoldingOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box mb={2}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Proof of NRI Document Type</InputLabel>
                                <Select
                                    name="proof_of_nri_document_type"
                                    value={currentState.proof_of_nri_document_type}
                                    onChange={handleChange}
                                    label="Proof of NRI Document Type"
                                >
                                    {proofOfNriDocumentTypeOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box mb={2}>
                            <TextField
                                type="text"
                                name="proof_of_nri_document_password"
                                variant="outlined"
                                fullWidth
                                label="Proof of NRI Document Password - If Blank, then no password is set"
                                onChange={handleChange}
                                value={currentState.proof_of_nri_document_password}
                            />
                        </Box>

                        <Box mb={3}>
                            <Typography variant="subtitle2" gutterBottom>
                                Upload Proof of NRI Document
                            </Typography>

                            <Box mb={2}>
                                <ImageUpload setFile={handleNriFileChange} />
                            </Box>

                            {/* Upload Time Preview */}
                            {proofOfNriFile && (
                                <Box
                                    width="100%"
                                    p={2}
                                    border="1px solid #e0e0e0"
                                    borderRadius={2}
                                    textAlign="center"
                                    mb={2}
                                >
                                    <Typography variant="subtitle2" gutterBottom>
                                        Selected File Preview
                                    </Typography>

                                    {proofOfNriFile.type === "application/pdf" ? (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <PictureAsPdfIcon
                                                sx={{ fontSize: 100, color: "#d32f2f", mb: 2 }}
                                            />

                                            <Typography variant="body2" gutterBottom>
                                                {proofOfNriFile.name}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <img
                                            src={URL.createObjectURL(proofOfNriFile)}
                                            alt="Selected NRI Preview"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "260px",
                                                objectFit: "contain",
                                                borderRadius: "8px",
                                                border: "1px solid #ddd",
                                            }}
                                        />
                                    )}

                                    <Typography
                                        variant="body2"
                                        style={{ color: "green", marginTop: 8 }}
                                    >
                                        New file selected: {proofOfNriFile.name}
                                    </Typography>
                                </Box>
                            )}

                            {/* GET Time Preview */}
                            {!proofOfNriFile &&
                                props?.aadharCardDetails?.proof_of_nri_document &&
                                (() => {
                                    const base64 =
                                        props.aadharCardDetails.proof_of_nri_document;
                                    const mimeType = getBase64MimeType(base64);

                                    return (
                                        <Box
                                            width="100%"
                                            p={2}
                                            border="1px solid #e0e0e0"
                                            borderRadius={2}
                                            textAlign="center"
                                        >
                                            <Typography variant="subtitle2" gutterBottom>
                                                Uploaded Document Preview
                                            </Typography>

                                            {mimeType === "application/pdf" ? (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <PictureAsPdfIcon
                                                        sx={{
                                                            fontSize: 100,
                                                            color: "#d32f2f",
                                                            mb: 2,
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        gutterBottom
                                                    >
                                                        PDF Document Uploaded
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <img
                                                    src={`data:${mimeType};base64,${base64}`}
                                                    alt="Proof of NRI"
                                                    style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "260px",
                                                        objectFit: "contain",
                                                        borderRadius: "8px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                />
                                            )}

                                            <Box
                                                mt={2}
                                                display="flex"
                                                justifyContent="center"
                                                gap={2}
                                                flexWrap="wrap"
                                            >
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={openNriDocument}
                                                >
                                                    View
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={downloadNriDocument}
                                                >
                                                    Download
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })()}
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={(event) => {
                                event.preventDefault();
                                props.saveProofOfNRI(event, buildNriPayload());
                            }}
                        >
                            Update
                        </Button>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default ProofOfNRIInformationGrid;
